import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, Table, Card, Spinner, InputGroup } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";

export default function D300Edit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preluareLoading, setPreluareLoading] = useState(false);
  const [importingMatrita, setImportingMatrita] = useState(false);
  const [importingXML, setImportingXML] = useState(false);

  const [main, setMain] = useState({
    bxluna: "",
    bxan: "",
    cui: "",
    firma: "",
    adresa: "",
    telefon: "",
    fax: "",
    email: "",
    banca: "",
    cont: "",
    reprezentant: "",
    nume: "",
    prenume: "",
    functia: "",
    simplificat: "",
    tip_decont: "",
    temei: "",
    prorata: "",
    numar_ev_plata: "",
    suma_control: 0
  });

  const [lines, setLines] = useState([]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Load Main
      const mainData = await authFetch(`/d300/${id}`);
      if (mainData && mainData.length > 0) {
        setMain(mainData[0]);
      } else {
        toast.error("Decontul nu a fost găsit.");
        navigate("/d300");
        return;
      }

      // 2. Load Details (Lines)
      const linesData = await authFetch(`/d300/detail/${id}`);
      setLines(linesData || []);
    } catch (e) {
      toast.error(`Eroare la încărcarea datelor: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const handleMainChange = (e) => {
    const { name, value } = e.target;
    setMain((prev) => ({ ...prev, [name]: value }));
  };

  const handleLineChange = (index, field, value) => {
    const updated = [...lines];
    updated[index][field] = value;
    setLines(updated);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...main,
        details: lines
      };
      const res = await authFetch("/d300/", {
        method: "POST",
        body: JSON.stringify(payload)
      });
      if (res.result === "pass") {
        toast.success("Decontul a fost salvat cu succes!");
        loadData();
      } else {
        toast.error(res.message || "Eroare la salvarea decontului.");
      }
    } catch (err) {
      toast.error(`Eroare la salvare: ${err?.message || err}`);
    } finally {
      setSaving(false);
    }
  };

  const handlePreluare = async () => {
    if (!window.confirm("Sigur doriți să recalculați valorile din tranzacțiile de intrări și ieșiri ale lunii? Acest lucru va suprascrie valorile actuale.")) return;
    setPreluareLoading(true);
    try {
      const res = await authFetch(`/d300/${id}/preluare`, {
        method: "POST",
        body: JSON.stringify({ bxluna: main.bxluna, bxan: main.bxan })
      });
      if (res.result === "pass") {
        toast.success("Date preluate și calculate cu succes!");
        // Reload lines
        const linesData = await authFetch(`/d300/detail/${id}`);
        setLines(linesData || []);
      } else {
        toast.error(res.message || "Eroare la preluarea datelor.");
      }
    } catch (err) {
      toast.error(`Eroare la preluarea datelor: ${err?.message || err}`);
    } finally {
      setPreluareLoading(false);
    }
  };

  const handleImportMatrita = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportingMatrita(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await authFetch("/d300/import-matrita", {
        method: "POST",
        body: formData,
        headers: {
          // fetch handles multipart boundary automatically when body is FormData
          "Content-Type": "" 
        }
      });
      
      if (res.result === "pass") {
        toast.success(res.message || "Matriță importată cu succes!");
        loadData();
      } else {
        toast.error(res.message || "Eroare la importul matriței.");
      }
    } catch (err) {
      toast.error(`Eroare la import matriță: ${err?.message || err}`);
    } finally {
      setImportingMatrita(false);
      // Reset file input
      e.target.value = null;
    }
  };

  const handleImportXML = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportingXML(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await authFetch(`/d300/${id}/import-xml`, {
        method: "POST",
        body: formData,
        headers: {
          "Content-Type": "" 
        }
      });
      
      if (res.result === "pass") {
        toast.success(res.message || "Datele din XML au fost importate cu succes!");
        loadData();
      } else {
        toast.error(res.message || "Eroare la importul XML.");
      }
    } catch (err) {
      toast.error(`Eroare la import XML: ${err?.message || err}`);
    } finally {
      setImportingXML(false);
      e.target.value = null;
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Se încarcă decontul D300...</p>
      </div>
    );
  }

  return (
    <Container className="mt-4 pb-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          Editare Decont D300 (Luna {main.bxluna} / Anul {main.bxan})
        </h2>
        <div>
          <Button variant="outline-secondary" onClick={() => navigate("/d300")} className="me-2">
            Înapoi la listă
          </Button>
          <Button variant="warning" onClick={handlePreluare} disabled={preluareLoading} className="me-2 text-white">
            {preluareLoading ? <Spinner size="sm" /> : "Preluare / Recalculare Date"}
          </Button>
          <Button variant="success" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Salvează modificări"}
          </Button>
        </div>
      </div>

      <Row>
        <Col lg={4}>
          <Card className="mb-4 shadow-sm border-0">
            <Card.Header className="bg-dark text-white fw-bold">Date Identificare Firmă</Card.Header>
            <Card.Body>
              <Form.Group className="mb-2">
                <Form.Label>Denumire Firmă</Form.Label>
                <Form.Control name="firma" value={main.firma || ""} onChange={handleMainChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>CUI / CIF</Form.Label>
                <Form.Control name="cui" value={main.cui || ""} onChange={handleMainChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Adresă completă</Form.Label>
                <Form.Control as="textarea" rows={2} name="adresa" value={main.adresa || ""} onChange={handleMainChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Telefon</Form.Label>
                <Form.Control name="telefon" value={main.telefon || ""} onChange={handleMainChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Email</Form.Label>
                <Form.Control name="email" value={main.email || ""} onChange={handleMainChange} />
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Bancă</Form.Label>
                    <Form.Control name="banca" value={main.banca || ""} onChange={handleMainChange} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Cont IBAN</Form.Label>
                    <Form.Control name="cont" value={main.cont || ""} onChange={handleMainChange} />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-4 shadow-sm border-0">
            <Card.Header className="bg-dark text-white fw-bold">Reprezentant Legal</Card.Header>
            <Card.Body>
              <Form.Group className="mb-2">
                <Form.Label>Calitate reprezentant</Form.Label>
                <Form.Control name="reprezentant" value={main.reprezentant || ""} onChange={handleMainChange} placeholder="Ex: Administrator" />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Nume</Form.Label>
                <Form.Control name="nume" value={main.nume || ""} onChange={handleMainChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Prenume</Form.Label>
                <Form.Control name="prenume" value={main.prenume || ""} onChange={handleMainChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Funcția</Form.Label>
                <Form.Control name="functia" value={main.functia || ""} onChange={handleMainChange} />
              </Form.Group>
            </Card.Body>
          </Card>

          <Card className="mb-4 shadow-sm border-0 bg-light">
            <Card.Body>
              <Form.Group>
                <Form.Label className="fw-bold text-secondary">Import Model Matriță (Excel / XML)</Form.Label>
                <Form.Control type="file" accept=".xlsx, .xls, .xml" onChange={handleImportMatrita} disabled={importingMatrita} />
                <Form.Text className="text-muted">
                  Alegeți un fișier Excel (RAND, DENUMIRE, CONT) sau XML (ANAF). Acesta va înlocui șablonul curent.
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>

          <Card className="mb-4 shadow-sm border-0 bg-light">
            <Card.Body>
              <Form.Group>
                <Form.Label className="fw-bold text-secondary">Import Date decont din XML (ANAF)</Form.Label>
                <Form.Control type="file" accept=".xml" onChange={handleImportXML} disabled={importingXML} />
                <Form.Text className="text-muted">
                  Alegeți un fișier XML de declarație D300 exportat. Acesta va completa datele firmei și valorile curente ale decontului.
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
              <span className="fw-bold">Rânduri Decont D300</span>
              <span className="badge bg-secondary">Total: {lines.length} rânduri</span>
            </Card.Header>
            <Card.Body className="p-0">
              <div style={{ maxHeight: "700px", overflowY: "auto" }}>
                <Table responsive striped hover className="mb-0">
                  <thead className="bg-light sticky-top">
                    <tr>
                      <th style={{ width: "80px" }}>Rând</th>
                      <th>Denumire Indicator</th>
                      <th style={{ width: "130px" }}>Valoare (Bază)</th>
                      <th style={{ width: "130px" }}>TVA (Taxă)</th>
                      <th style={{ width: "100px" }}>Conturi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((ln, idx) => (
                      <tr key={ln.bxid || idx}>
                        <td className="align-middle fw-bold text-center bg-light">{ln.rand}</td>
                        <td className="align-middle small">{ln.denumire}</td>
                        <td className="align-middle">
                          <Form.Control
                            type="number"
                            size="sm"
                            step="0.01"
                            value={ln.valoare}
                            onChange={(e) => handleLineChange(idx, "valoare", parseFloat(e.target.value) || 0.0)}
                            className="text-end"
                          />
                        </td>
                        <td className="align-middle">
                          <Form.Control
                            type="number"
                            size="sm"
                            step="0.01"
                            value={ln.tva}
                            onChange={(e) => handleLineChange(idx, "tva", parseFloat(e.target.value) || 0.0)}
                            className="text-end"
                          />
                        </td>
                        <td className="align-middle">
                          <Form.Control
                            size="sm"
                            value={ln.cont || ""}
                            onChange={(e) => handleLineChange(idx, "cont", e.target.value)}
                            className="text-center font-monospace"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
